import { defineStore } from "pinia"
import { computed, inject, ref } from "vue"
import i18next from "i18next"
import type { Translate, Locale } from "@/plugins/i18n/types"
import { getLocales } from "@/api/s3"
import { useCookies } from "@vueuse/integrations/useCookies"
import { defaultLocale } from "@/config/i18n"
import { useUserStore } from "@/stores/user"
import { useHelpStore } from "@/components/af/Help/help.store"

// @TODO: tenter de refactoriser pour importer seulement le store nécessaire
import { useQuestionsStore as useSwipeQuestionsStore } from "@/features/swipe/store/questions.store"
import { useQuestionsStore as useDriveQuestionsStore } from "@/features/drive/store/questions.store"
import { useQuestionsStore as useVoiceQuestionsStore } from "@/features/voice/store/questions.store"
import { useAnswersStore } from "@/features/brain/stores/answers.store"

export const useI18nStore = defineStore("i18n", () => {
  const helpStore = useHelpStore()
  const t = inject("t") as Translate

  const showLang = ref(true)
  /**
   * Verrou posé pendant l'animation de chargement d'un assessment.
   *
   * Distinct de `showLang` volontairement : chaque écran décide librement de
   * `showLang` (« ce test est commencé, la langue est figée »), et les
   * chargeurs prennent cette décision dès que l'API répond, bien avant la fin
   * de l'animation. Écraser `showLang` pour masquer le sélecteur pendant ce
   * temps perdrait la décision de l'écran — les assessments dont le chargeur
   * ne la prend pas, comme move et movebis, resteraient verrouillés ensuite.
   * Le verrou se superpose donc au lieu de remplacer.
   */
  const isLangLocked = ref(false)
  const locales = ref<Locale[]>([])
  const loading = ref(false)
  const currentLocale = ref<Locale>(useCookies().get("locale") ?? defaultLocale)
  const isRTL = computed(() =>
    currentLocale.value?.iso_code_639?.includes("ar")
  )

  // GETTERS

  const currentLang = computed(() => {
    return currentLocale.value && currentLocale.value.iso_code_639.slice(0, 2)
  })

  const isCurrentLang = (lang: string) => {
    return currentLocale.value.iso_code_639 === lang
  }

  const isCurrentLangFr = () => {
    return currentLocale.value.iso_code_639.includes("fr")
  }

  // Each language name is translated into its own language.
  const languagesList = computed(() => {
    const list = locales.value?.map((locale) => {
      return {
        ...locale,
        title: t(getLanguageKey(locale.iso_code_639, false)),
      }
    })

    return list?.sort((a, b) => {
      if (a.title < b.title) return -1
      return 1
    })
  })

  // Each language name is translated into the auth's language.
  const localizedLanguagesList = computed(() => {
    const list = locales.value?.map((locale) => {
      return {
        ...locale,
        title: t(getLanguageKey(locale.iso_code_639, true)),
      }
    })

    return list?.sort((a, b) => {
      if (a.title < b.title) return -1
      return 1
    })
  })

  // ACTIONS

  const fetchLocales = async () => {
    locales.value = await getLocales()
  }

  async function updateLocale(localeId?: number) {
    loading.value = true

    if (!localeId) return

    await useUserStore().updateUser({ locale_id: localeId })
    await changeAppLocale(localeId)

    helpStore.stopAudio()
    helpStore.resetAudio()

    if (helpStore.name === "Swipe") {
      await useSwipeQuestionsStore().fetchAudios()
    }

    if (helpStore.name === "Drive") {
      await useDriveQuestionsStore().fetchAudios()
      await useDriveQuestionsStore().fetchQuestions() // @todo: delete this line when we can use translation keys directly in questions
    }

    if (helpStore.name === "Brain") {
      await useAnswersStore().fetchAudios()
    }

    if (helpStore.name === "Voice") {
      await useVoiceQuestionsStore().fetchAudios()
    }

    loading.value = false
  }

  async function changeAppLocale(localeId: number) {
    const locale = getLocaleById(localeId)

    await i18next.changeLanguage(locale.iso_code_639, () => {
      currentLocale.value = locale
      useCookies().set("locale", locale)
      document.documentElement.lang = locale.iso_code_639
      document.documentElement.dir = locale.iso_code_639.includes("ar")
        ? "rtl"
        : "ltr"
    })
  }

  function getLanguageKey(isoCode: string, localized = false) {
    //@todo: remove condition when chinese issue fixed, maybe one day :shrugging_man:
    const formattedIsoCode = isoCode.includes("zh")
      ? "zh_cn"
      : isoCode.toLowerCase().replace("-", "_")

    return `language${localized ? ".localized" : ""}.${formattedIsoCode}`
  }

  function getLocaleById(id: number) {
    const locale =
      locales.value && locales.value.find((locale) => locale.id === id)

    return locale ?? defaultLocale
  }

  function getLocaleIdFromIsoCode(iso_code: string) {
    const locale =
      locales.value &&
      locales.value.find((locale) => locale.iso_code === iso_code)
    return locale?.id ?? defaultLocale.id
  }

  return {
    showLang,
    isLangLocked,
    locales,
    loading,
    currentLocale,
    currentLang,
    languagesList,
    localizedLanguagesList,
    isCurrentLang,
    isCurrentLangFr,
    getLanguageKey,
    getLocaleById,
    getLocaleIdFromIsoCode,
    fetchLocales,
    updateLocale,
    changeAppLocale,
    isRTL,
  }
})
